const SECRET_KEY = 'MySecretKey2024!@#$%';
export const SECURE_PAYLOAD_VERSION = 1 as const;
export const SECURE_PAYLOAD_ALG = 'AES-GCM' as const;

export interface SecurePayloadEnvelope {
  alg: typeof SECURE_PAYLOAD_ALG;
  v: typeof SECURE_PAYLOAD_VERSION;
  iv: string;
  data: string;
}

let cachedKey: CryptoKey | null = null;

async function deriveAesKey(): Promise<CryptoKey> {
  if (cachedKey) return cachedKey;
  const secretBytes = new TextEncoder().encode(SECRET_KEY);
  const hash = await crypto.subtle.digest('SHA-256', secretBytes);
  cachedKey = await crypto.subtle.importKey('raw', hash, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]);
  return cachedKey;
}

function fromBase64(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

export function isSecurePayloadEnvelope(value: unknown): value is SecurePayloadEnvelope {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    v.alg === SECURE_PAYLOAD_ALG &&
    v.v === SECURE_PAYLOAD_VERSION &&
    typeof v.iv === 'string' &&
    typeof v.data === 'string'
  );
}

export async function decryptJsonPayload<T = unknown>(envelope: SecurePayloadEnvelope): Promise<T> {
  const key = await deriveAesKey();
  const iv = fromBase64(envelope.iv);
  const data = fromBase64(envelope.data);
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    data as BufferSource,
  );
  return JSON.parse(new TextDecoder().decode(plaintext)) as T;
}
